import { Nav } from "react-bootstrap";
import { NavLink } from "react-router-dom";
import type { UnpackResponse } from "../../../api/src/lib";
import type { ProfileController } from "../../../api/src/profile/profile.controller";
import { useAuthenticatedFetch } from "../lib/api";

/**
 * The firm settings tabs.
 *
 * Every tab but one is shown to everybody in the firm. Billing Fields is the
 * exception: that page reads and creates the Slant custom fields through the
 * LOOKING user's own Personal Access Token, so a user without one cannot use it
 * whatever the firm has set up (APP-1437). `connections.slant` is exactly that
 * token — `slant_api_key` for this user — and it is the same flag the send
 * control on HouseholdCashBilling is shown on.
 *
 * The read is deliberately the plain `/profile` query rather than a new
 * endpoint: `["/profile"]` is the default query key and several other pages
 * already populate it, so a warm cache renders the tabs with the flag already
 * in hand. It is not free — the app's QueryClient sets no `staleTime`, so
 * mounting here still issues a background re-read — but nothing waits on it.
 *
 * While the read is in flight the link is simply absent, which on a genuinely
 * cold load shows the tab arriving a beat after the others. That is preferred
 * to a placeholder tab, which would have to lead somewhere before the answer is
 * known. Hiding the link is not the gate in any case — the page and the api
 * both refuse a user without a PAT.
 */
const FirmNav = () => {
  const { data: profileBody } =
    useAuthenticatedFetch<UnpackResponse<ProfileController["getProfile"]>>(
      "/profile",
    );
  const slantConnected = profileBody?.data.connections.slant === true;

  return (
    <nav>
      <Nav variant="tabs" defaultActiveKey="/firm/info">
        <Nav.Item>
          <NavLink to="/firm/info" className="nav-link">
            Settings
          </NavLink>
        </Nav.Item>
        <Nav.Item>
          <NavLink to="/firm/security-master" className="nav-link">
            Security Master
          </NavLink>
        </Nav.Item>
        <Nav.Item>
          <NavLink to="/firm/risk" className="nav-link" end>
            Risk
          </NavLink>
        </Nav.Item>
        <Nav.Item>
          <NavLink to="/firm/risk/asset-class" className="nav-link">
            Asset Class
          </NavLink>
        </Nav.Item>
        <Nav.Item>
          <NavLink to="/firm/risk/security-category" className="nav-link">
            Security Category
          </NavLink>
        </Nav.Item>
        <Nav.Item>
          <NavLink to="/firm/trades/minimum-size" className="nav-link">
            Minimum Trade Sizes
          </NavLink>
        </Nav.Item>
        {slantConnected ? (
          <Nav.Item>
            <NavLink to="/firm/slant-billing-fields" className="nav-link">
              Billing Fields
            </NavLink>
          </Nav.Item>
        ) : null}
        <Nav.Item>
          <NavLink to="/firm/activity" className="nav-link">
            Activity
          </NavLink>
        </Nav.Item>
      </Nav>
    </nav>
  );
};

export default FirmNav;
